import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TreeNodeData } from '../model';
import { Demo } from '../app/demo';
import { ReferencedTree } from '../ReferencedTree';
import { TreeStore } from '../store';

export const treeContext = createContext<TreeContextType | null>(null);

export type TreeContextType = {
  tree_data: TreeNodeData;
  move: (
    id: number,
    target: number,
    location: 'before' | 'inside' | 'after',
    path: number[],
  ) => void;
  insertAtNode: (
    target: number,
    location: 'before' | 'inside' | 'after',
  ) => void;
  setNodeText: (target: number, text: string) => void;
  deleteNode: (node_id: number) => void;
};

export function TreeContextProvider({
  children,
  initialData,
}: React.PropsWithChildren<{ initialData: TreeNodeData }>) {
  // TODO: Don't use demo in real life.
  const [currentTree, setCurrentTree] = useState<ReferencedTree>(
    new ReferencedTree(initialData, 0),
  );

  const saveTree = useCallback(
    (v: (a: ReferencedTree) => ReferencedTree) => {
      setCurrentTree((c) => {
        const r = v(c);
        TreeStore.update(r.root);
        return r;
      });
    },
    [setCurrentTree],
  );

  const value = useMemo(
    () =>
      ({
        tree_data: currentTree.root,
        move: (id, target, location) => {
          if (location === 'inside') {
            saveTree((c) => {
              const result = ReferencedTree.clone(c.moveNode(id, target, 0));
              return result;
            });
          } else if (location === 'before') {
            saveTree((c) => {
              const ref = c.getIndexInParent(target);
              const result = ReferencedTree.clone(
                c.moveNode(id, ref.parent.id, ref.index),
              );

              return result;
            });
          } else {
            saveTree((c) => {
              const ref = c.getIndexInParent(target);
              const result = ReferencedTree.clone(
                c.moveNode(id, ref.parent.id, ref.index + 1),
              );

              return result;
            });
          }
          console.warn('MOVE CALLED', id, target, location);
        },
        insertAtNode: (
          target: number,
          location: 'before' | 'inside' | 'after',
        ) => {
          saveTree((c) => {
            if (location === 'inside') {
              return ReferencedTree.clone(c.insertNode(target, 0));
            }
            const ref = c.getIndexInParent(target);
            if (location === 'before') {
              return ReferencedTree.clone(
                c.insertNode(ref.parent.id, ref.index),
              );
            } else {
              return ReferencedTree.clone(
                c.insertNode(ref.parent.id, ref.index + 1),
              );
            }
          });
        },
        setNodeText: (target: number, text: string) => {
          saveTree((c) => {
            return ReferencedTree.clone(c.setNodeText(target, text));
          });
        },
        deleteNode: (node_id: number) => {
          saveTree((c) => {
            return ReferencedTree.clone(c.deleteNode(node_id));
          });
        },
      }) as TreeContextType,
    [currentTree],
  );

  return <treeContext.Provider value={value}>{children}</treeContext.Provider>;
}

export function useTreeContext() {
  const exp = useContext(treeContext);

  if (!exp) {
    throw new Error(`Cannot useTreeContext from outside a provider`);
  }

  return exp;
}
